/**
 * Заголовок, описание, канонический адрес и микроразметка страницы.
 *
 * Сайт — SPA: документ один на все адреса, и без этого в выдаче все страницы
 * выглядели бы одинаково — с текстом главной. Тег <title> и описание правятся
 * на каждом переходе роутера, канонический адрес всегда указывает на основной
 * домен (www), чтобы обращения к голому домену не плодили дубли.
 */
import { useEffect } from 'react';

/** Основной адрес сайта. Отсюда строятся canonical и адреса в разметке. */
export const SITE_URL = 'https://www.freedombiker.kz';

function upsertMeta(selector: string, attrs: Record<string, string>): void {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement('meta');
    document.head.appendChild(tag);
  }
  for (const [name, value] of Object.entries(attrs)) tag.setAttribute(name, value);
}

function upsertLink(rel: string, href: string): void {
  let tag = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.rel = rel;
    document.head.appendChild(tag);
  }
  tag.href = href;
}

interface PageMeta {
  title: string;
  description: string;
  /** Путь без домена, например `/schedule`. */
  path: string;
  /** Закрыть страницу от индексации: кабинет, вход, одноразовые ссылки. */
  noindex?: boolean;
}

export function usePageMeta({ title, description, path, noindex }: PageMeta): void {
  useEffect(() => {
    document.title = title;
    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertLink('canonical', `${SITE_URL}${path}`);

    // og-теги дублируют то же самое: превью в мессенджерах собирается из них
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: description,
    });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: `${SITE_URL}${path}` });

    if (noindex) {
      upsertMeta('meta[name="robots"]', { name: 'robots', content: 'noindex, nofollow' });
    } else {
      document.head.querySelector('meta[name="robots"]')?.remove();
    }
  }, [title, description, path, noindex]);
}

/**
 * Микроразметка школы для поисковиков: адрес, координаты площадки, телефон,
 * ссылки на соцсети. По ней Google и Яндекс понимают, что это локальная
 * автошкола в Алматы, а не абстрактный сайт.
 *
 * Данные — из настроек школы, чтобы разметка не разошлась с тем, что видит
 * человек на странице: расхождение поисковики считают попыткой обмана.
 */
export function useSchoolSchema(data: {
  name: string;
  phone: string | null;
  instagram: string | null;
  isBookingOpen: boolean;
  faq: Array<{ question: string; answer: string }>;
  geo: { lat: number; lon: number; city: string };
}): void {
  useEffect(() => {
    const school: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'DrivingSchool',
      name: data.name,
      description:
        'Мотошкола в Алматы: обучение вождению мотоцикла с инструктором на площадке, ' +
        'категории A и A1, занятия один на один.',
      url: SITE_URL,
      image: `${SITE_URL}/og.jpg`,
      areaServed: { '@type': 'City', name: data.geo.city },
      address: { '@type': 'PostalAddress', addressLocality: data.geo.city, addressCountry: 'KZ' },
      geo: { '@type': 'GeoCoordinates', latitude: data.geo.lat, longitude: data.geo.lon },
      /*
       * Курсы перечислены отдельно: по ним поисковик связывает школу
       * с запросами «категория A» и «A1», которых в тексте страницы мало.
       */
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Обучение вождению мотоцикла',
        itemListElement: ['Категория A', 'Категория A1'].map((category) => ({
          '@type': 'Course',
          name: `Обучение вождению мотоцикла, ${category}`,
          provider: { '@type': 'DrivingSchool', name: data.name },
        })),
      },
    };

    if (data.phone) school.telephone = data.phone;
    if (data.instagram) school.sameAs = [`https://www.instagram.com/${data.instagram}`];

    const faq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: data.faq.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.seo = 'school';
    script.textContent = JSON.stringify([school, faq]);
    document.head.appendChild(script);

    return () => script.remove();
  }, [data.name, data.phone, data.instagram, data.isBookingOpen, data.faq, data.geo]);
}
